import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { products } from '../data/mock';
import { Helmet } from 'react-helmet';

const Products = () => {
  const featuredProduct = products.find(p => p.featured);
  const otherProducts = products.filter(p => !p.featured);

  const scrollToQuote = () => {
    const element = document.getElementById('quote');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Générer les données structurées Schema.org pour chaque produit
  const generateProductSchema = (product) => {
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.image.startsWith('http') ? product.image : `https://orion-industrie.eu${product.image}`,
      "brand": {
        "@type": "Brand",
        "name": product.id === 4 ? "Altech" : "Orion Industrie"  // Ponts Alu = Altech, reste = Orion Industrie
      },
      "offers": {
        "@type": "Offer",
        "url": "https://orion-industrie.eu#products",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/PreOrder",
        "itemCondition": "https://schema.org/NewCondition"
      }
    };

    // Ajouter le prix (obligatoire pour Google)
    if (product.price !== 'sur_devis') {
      schema.offers.price = product.price;
      schema.offers.priceValidUntil = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
    } else {
      // Pour les produits "sur devis", on met 0 et on indique que c'est sur demande
      schema.offers.price = "0";
      schema.offers.priceSpecification = {
        "@type": "PriceSpecification",
        "priceCurrency": "EUR",
        "price": "0",
        "valueAddedTaxIncluded": false
      };
      // Indiquer que le prix est disponible sur demande de devis
      schema.offers.description = "Prix sur devis - Contactez-nous pour un tarif personnalisé";
    }

    // Ajouter les caractéristiques
    if (product.features && product.features.length > 0) {
      schema.additionalProperty = product.features.map(feature => ({
        "@type": "PropertyValue",
        "name": "Feature",
        "value": feature
      }));
    }

    return schema;
  };

  // Créer un tableau de tous les produits pour Schema.org
  const allProductsSchema = products.map(generateProductSchema);

  return (
    <section id="products" className="py-24 bg-white">
      {/* Schema.org structured data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(allProductsSchema)}
        </script>
      </Helmet>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Notre Gamme</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-4">
            Nos Équipements de Quai
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Solutions complètes pour vos installations logistiques, dans le respect des normes de sécurité au travail
          </p>
        </div>

        {/* Featured Product - Pont Hydraulique */}
        {featuredProduct && (
          <div className="mb-20">
            <div className="bg-gradient-to-br from-[#020d2a] to-[#03152f] rounded-2xl overflow-hidden shadow-2xl">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative h-96 lg:h-auto enhanced-product-image">
                  <img 
                    src={featuredProduct.image}
                    alt={featuredProduct.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-#FF6600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      Produit Phare
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-12">
                  <span className="#FF6600 font-semibold text-sm uppercase tracking-wider">
                    {featuredProduct.category}
                  </span>
                  <h3 className="text-4xl font-bold text-white mt-3 mb-6">
                    {featuredProduct.name}
                  </h3>
                  
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    {featuredProduct.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {featuredProduct.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[#FF6600] flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    onClick={scrollToQuote}
                    size="lg"
                    className="bg-#FF6600 hover:bg-#E65A00 text-white group"
                  >
                    Demander un devis
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other Products Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {otherProducts.map((product) => (
            <div 
              key={product.id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-200 group"
            >
              {/* Product Image with Multiple Images Support */}
              <div className="relative h-64 overflow-hidden bg-gray-100">
                {product.hasMultipleImages && product.images ? (
                  // Grid layout pour 2 images côte à côte pour les barrières
                  <div className="grid grid-cols-2 gap-1 h-full p-1">
                    {product.images.map((img, idx) => (
                      <div key={idx} className="relative overflow-hidden rounded-md">
                        <img 
                          src={img.url} 
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute bottom-1 left-1 right-1">
                          <span className="text-xs font-medium px-1.5 py-0.5 bg-[#FF6600]/90 text-white rounded backdrop-blur-sm block text-center">
                            {img.type === 'pivotante' ? 'Pivotante' : 'Levante'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020d2a]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <span className="#FF6600 font-semibold text-sm">
                        {product.category}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Product Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {product.name}
                </h3>
                
                {/* Prix */}
                <div className="mb-4 #FF660050 rounded-lg p-3 border-l-4 border-#FF6600">
                  {product.price === 'sur_devis' ? (
                    <p className="text-2xl font-bold text-[#E65A00]">
                      Prix sur devis
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">{product.priceLabel}</p>
                      <p className="text-3xl font-bold text-[#E65A00]">
                        {product.price} <span className="text-lg">{product.priceUnit}</span>
                      </p>
                    </>
                  )}
                </div>
                
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  {product.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#FF6600] flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={scrollToQuote}
                  className="w-full bg-#FF6600 hover:bg-#E65A00 text-white font-semibold"
                >
                  Demander un devis
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-20 bg-gradient-to-r from-[#FF6600] to-[#E65A00] rounded-2xl p-12 text-center shadow-2xl">
          <h3 className="text-3xl font-bold text-white mb-4">
            Besoin d'un équipement spécifique ?
          </h3>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            Nous proposons également des solutions sur mesure adaptées à vos contraintes spécifiques. Contactez-nous pour en discuter.
          </p>
          <Button 
            onClick={scrollToQuote}
            size="lg"
            className="bg-white text-[#FF6600] hover:bg-gray-100"
          >
            Demander un devis personnalisé
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Products;
